import {
   EAC_COLD_LEAD,
   EAC_NEW_LEAD,
   MIN_TOP_UP,
} from '@forwardflow/constants/onboarding.constants'

/** Share of the monthly budget that goes to new leads; the rest revives older ones. */
const NEW_LEAD_SHARE = 0.62

export const MIN_BUDGET = 50
export const MAX_BUDGET = 5000
/** The default: the smallest amount that is also the minimum starting balance. */
export const DEFAULT_BUDGET = MIN_TOP_UP

export interface IBudgetPreset {
   id: 'starter' | 'growth' | 'scale'
   label: string
   maxSpend: number
   blurb: string
}

export const BUDGET_PRESETS: IBudgetPreset[] = [
   {
      id: 'starter',
      label: 'Starter',
      maxSpend: 200,
      blurb: 'A steady start on new leads',
   },
   {
      id: 'growth',
      label: 'Growth',
      maxSpend: 300,
      blurb: 'New leads plus a revival push',
   },
   {
      id: 'scale',
      label: 'Scale',
      maxSpend: 500,
      blurb: 'Every lead, every day',
   },
]

export const clampBudget = (value: number) =>
   Math.min(MAX_BUDGET, Math.max(MIN_BUDGET, Math.round(value || MIN_BUDGET)))

/** New-lead and revival budgets for a monthly amount, the engine's own split. */
export const splitBudget = (maxSpend: number) => {
   const total = Math.max(0, Math.round(maxSpend))
   const newLeadBudget = Math.round(total * NEW_LEAD_SHARE)
   return {
      maxSpend: total,
      newLeadBudget,
      reactivateLeadBudget: total - newLeadBudget,
   }
}

/** About how many lead conversations a monthly budget buys, at the engine's cost per lead. */
export const leadsFor = (maxSpend: number) => {
   const { newLeadBudget, reactivateLeadBudget } = splitBudget(maxSpend)
   return (
      Math.floor(newLeadBudget / EAC_NEW_LEAD) +
      Math.floor(reactivateLeadBudget / EAC_COLD_LEAD)
   )
}

/** What the card is charged on day one: the seats plus a starting balance of at least the minimum top-up. */
export const startingBalanceFor = (maxSpend: number) =>
   Math.max(MIN_TOP_UP, maxSpend)

export interface IPricing {
   selectedTierId: number | null
   seatsCount: number
   newLeadBudget: number
   reactivateLeadBudget: number
   maxSpend: number
}

/** The record's pricing with the defaults filled in: one seat, the default budget. Old records have none. */
export const pricingOf = (
   pricing: Partial<IPricing> | null | undefined
): IPricing => ({
   selectedTierId: pricing?.selectedTierId ?? null,
   seatsCount:
      pricing?.seatsCount && pricing.seatsCount > 0 ? pricing.seatsCount : 1,
   ...splitBudget(
      pricing?.maxSpend && pricing.maxSpend > 0
         ? pricing.maxSpend
         : DEFAULT_BUDGET
   ),
   ...(pricing?.maxSpend &&
   pricing.maxSpend > 0 &&
   typeof pricing.newLeadBudget === 'number' &&
   typeof pricing.reactivateLeadBudget === 'number'
      ? {
           newLeadBudget: pricing.newLeadBudget,
           reactivateLeadBudget: pricing.reactivateLeadBudget,
        }
      : {}),
})
